import API from './API';

// -----------------------------------------------------------------------------
//  Pacchetto valet a listino — stesso modello del
//  gestionale partner (`nuovoClienteAPI.js` e `PropostaValet.vue` là).
//
//  Il prezzo non si scrive più a mano:
//    1. distanza su strada casa del cliente → sede del partner
//       (`POST /valet-km-band/quote`, Simba indica il partner);
//    2. fascia del listino → prezzo Simba di UN pickup all'anno, per pacchetti
//       da 1, 2 o 3 pickup all'anno;
//    3. pickup all'anno e durata in mesi → totale, rata, valet compresi.
//  Sul pacchetto si salva SOLO il prezzo Simba. Importi senza IVA.
// -----------------------------------------------------------------------------

export const IVA = 0.22;
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const rows = (r) => (Array.isArray(r.data) ? r.data : r.data?.data ?? []);

/** Oggi + `months` mesi. */
export function addMonths(months, from = new Date()) {
  const d = new Date(from);
  d.setMonth(d.getMonth() + Number(months || 0));
  return d;
}

/** Sedi di un partner: la sede dà la distanza dalla casa del cliente. */
export function headquartersOfPartner(partnerId) {
  return API.get('/headquarter', {
    params: { s: JSON.stringify({ partnerId: { $eq: partnerId } }), sort: 'name,ASC', limit: 200 },
  }).then(rows);
}

/** Preventivo del listino valet per un indirizzo e una sede del partner. */
export function quote({ partnerId, origin, headquarterId }) {
  return API.post(
    '/valet-km-band/quote',
    { partnerId, origin, headquarterId },
    { skipErrorToast: true },
  ).then((r) => r.data);
}

/**
 * I numeri di un pacchetto da una riga del preventivo.
 *
 * ⚠️ IL PREZZO NON DIPENDE DALLA DURATA. Le righe del preventivo sono prezzi
 * di UN pickup per fascia di volume (uno, due, tre o più): il pacchetto costa
 * `prezzo unitario × numero di pickup`, e i mesi decidono solo la scadenza.
 * I valet compresi sono i pickup comprati.
 *
 * `pickupsChosen` permette di comprarne un numero qualsiasi: sopra i tre vale
 * il prezzo unitario della terza riga.
 */
export function packageNumbers(option, months, pickupsChosen = null) {
  const tier = Number(option?.pickups_per_year) || 0;
  const pickup = Math.max(1, Math.floor(Number(pickupsChosen ?? tier) || 0));
  const m = Math.max(0, Math.floor(Number(months) || 0));
  const unit = (v) => (v === null || v === undefined ? null : round2(Number(v)));
  // `*_unit` è il campo nuovo; `*_annual` è il vecchio nome, stesso valore.
  const simbaUnit = unit(option?.simba_unit ?? option?.simba_annual);
  const sellerUnit = unit(option?.partner_unit ?? option?.partner_annual);
  const simbaTotal = simbaUnit === null ? null : round2(simbaUnit * pickup);
  const sellerTotal = sellerUnit === null ? null : round2(sellerUnit * pickup);
  // Quanto viene al mese sulla durata scelta. NON è il prezzo (che dipende
  // solo dai pickup): è la rateizzazione, quella che il cliente chiede sempre
  // davanti a un contratto di due o quattro anni.
  const perMonth = (total) => (total === null || m <= 0 ? null : round2(total / m));
  return {
    pickup,
    months: m,
    valetTotal: pickup,
    simbaUnit,
    simbaTotal,
    sellerUnit,
    sellerTotal,
    simbaMonthly: perMonth(simbaTotal),
    sellerMonthly: perMonth(sellerTotal),
    expiry: addMonths(m),
  };
}

/**
 * Il corpo di `POST /minute` per un pacchetto proposto e da confermare.
 * Con `valet_band_id` il backend ricalcola dalla fascia prezzo Simba, prezzo
 * del venditore e km compresi: quelli di qui sono l'anteprima.
 */
export function minutePayload({ carId, partnerId, pickup, months, valetTotal, simbaTotal, sellerTotal, bandId, includedKm }) {
  return {
    valet: valetTotal,
    residual_valet: valetTotal,
    valet_per_year: Number(pickup),
    duration_months: Number(months),
    duration_years: Math.ceil(Number(months) / 12),
    expiration_valet: addMonths(months),
    imponibile: simbaTotal,
    tot_fattura: round2(simbaTotal * (1 + IVA)),
    partner_imponibile: sellerTotal ?? null,
    valet_band_id: bandId ?? null,
    included_km: includedKm ?? null,
    // Niente `rif_fattura`: «non fatturato» è NULL. Uno 0 verrebbe salvato
    // come riferimento fattura "0" (la colonna è testo).
    esito: false,
    accepted: false,
    data: new Date(),
    car: { id: carId },
    partner: { id: partnerId },
  };
}

export default { IVA, addMonths, headquartersOfPartner, quote, packageNumbers, minutePayload };
